// Tiny synchronous signals primitive.
//
// Conventions:
//   signal(v)        — mutable reactive cell.
//   computed(fn)     — lazy derived signal; recomputes on .get() when a dep changed.
//   effect(fn)       — runs fn now and on every dep change; returns a dispose fn.
//   batch(fn)        — coalesces synchronous .set() calls into one downstream pass.
//   untracked(fn)    — runs fn without registering reads as deps.
//
// Notification model: synchronous + dynamic dep tracking. Cycles are caller bugs
// (will stack-overflow). batch() is the escape hatch for multi-set updates.

type Subscriber = {
  run: () => void;
  deps: Set<Set<Subscriber>>;
};

let active: Subscriber | null = null;
let batchDepth = 0;
let batchPending = new Set<Subscriber>();

function track(subs: Set<Subscriber>): void {
  if (active) {
    subs.add(active);
    active.deps.add(subs);
  }
}

function untrack(s: Subscriber): void {
  for (const set of s.deps) set.delete(s);
  s.deps.clear();
}

function notify(subs: Set<Subscriber>): void {
  if (batchDepth > 0) {
    for (const s of subs) batchPending.add(s);
  } else {
    // Snapshot — subscribers may add/remove during run.
    for (const s of [...subs]) s.run();
  }
}

export interface Signal<T> {
  get(): T;
  set(value: T): void;
  peek(): T;
  update(fn: (prev: T) => T): void;
}

export interface ReadSignal<T> {
  get(): T;
  peek(): T;
}

export type Equality<T> = (a: T, b: T) => boolean;

export function signal<T>(initial: T, eq: Equality<T> = Object.is): Signal<T> {
  let value = initial;
  const subs = new Set<Subscriber>();

  const api: Signal<T> = {
    get() {
      track(subs);
      return value;
    },
    peek() {
      return value;
    },
    set(next: T) {
      if (eq(value, next)) return;
      value = next;
      notify(subs);
    },
    update(fn: (prev: T) => T) {
      api.set(fn(value));
    },
  };
  return api;
}

export function effect(fn: () => void): () => void {
  let disposed = false;
  const sub: Subscriber = {
    run: () => {
      if (disposed) return;
      untrack(sub);
      const prev = active;
      active = sub;
      try {
        fn();
      } finally {
        active = prev;
      }
    },
    deps: new Set(),
  };
  sub.run();
  return () => {
    if (disposed) return;
    disposed = true;
    untrack(sub);
    batchPending.delete(sub);
  };
}

export function computed<T>(fn: () => T, eq: Equality<T> = Object.is): ReadSignal<T> {
  // First read seeds `value`; the dirty flag guards reads before that.
  let value: T;
  let dirty = true;
  const subs = new Set<Subscriber>();

  const self: Subscriber = {
    run: () => {
      if (dirty) return; // already marked; no need to re-notify
      dirty = true;
      notify(subs);
    },
    deps: new Set(),
  };

  function recompute(): void {
    untrack(self);
    const prev = active;
    active = self;
    try {
      const next = fn();
      // Suppress propagation if value unchanged after a recompute triggered by
      // a deeper change (subs already notified once on dirty-flip).
      if (!dirty || value === undefined || !eq(value, next)) {
        value = next;
      }
      dirty = false;
    } finally {
      active = prev;
    }
  }

  return {
    get() {
      track(subs);
      if (dirty) recompute();
      return value;
    },
    peek() {
      if (dirty) recompute();
      return value;
    },
  };
}

export function batch<T>(fn: () => T): T {
  batchDepth++;
  try {
    return fn();
  } finally {
    batchDepth--;
    if (batchDepth === 0 && batchPending.size > 0) {
      const subs = batchPending;
      batchPending = new Set();
      for (const s of subs) s.run();
    }
  }
}

export function untracked<T>(fn: () => T): T {
  const prev = active;
  active = null;
  try {
    return fn();
  } finally {
    active = prev;
  }
}
